import React, { useContext, useEffect, useRef, useState } from 'react';
import { DataTable, Notifications } from '../../../../shared';
import { ActionsCell } from './ActionsCell';
import { ActionModal } from './ActionModal';
import './locationInventoryV2Styles.css';
import { Button, CircularProgress } from '@material-ui/core';
import { ChangeExpirationDateAction } from './actions/changeExpirationDateAction';
import { MoveCasesAndLooseUnitsFromKnownOriginAction } from './actions/moveCasesAndLooseUnitsAction';
import { BulkAdjustUnitsPerCaseAction } from './actions/bulkAdjustUnitsPerCaseAction';
import { WarehouseInventoryV2Api } from '../../../../api/warehouseInventoryV2';
import { UserContext } from '../../../../contexts/UserContext';
import { PrintCaseLabelAction } from './actions/printCaseLabelAction';
import { AdjustCasesAndLooseUnitsAction } from './actions/adjustCasesAndLooseUnitsAction';
import { ViewPalletsModal } from './ViewPalletsModal';
import { UpdatePartnerLotAction } from './actions/updatePartnerLotAction';

const warehouseInventoryV2Api = new WarehouseInventoryV2Api();

const SkuTabExpansion = ({ onLinkClicked, externalSku, type, onSuccess }) => {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [viewActions, setViewActions] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [actions, setActions] = useState([]);
    const [loadingLots, setLoadingLots] = useState(false);
    const [selectedLocationName, setSelectedLocationName] = useState(null);
    const [selectedLotId, setSelectedLotId] = useState(null);
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const user = useContext(UserContext);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        getLotDataForSku();
    }, [externalSku]);

    //filter actions based on selectedRow.type
    useEffect(() => {
        setActions(actionDefinitions);
    }, [selectedRow]);

    const showPalletModal = async (row) => {
        setSelectedLocationName(row.locationName);
        setSelectedLotId(row.lotId);
        setSelectedLocationId(row.locationId);
    };

    const handleMovePalletSuccess = () => {
        getLotDataForSku();
    };

    const columns = [
        {
            Header: 'Lot Id',
            accessor: 'lotId',
            Cell: ({ row }) => {
                return (
                    <Button variant="text" color="primary" onClick={() => handleLotLinkClick(row.original.lotId)}>
                        {row.original.lotId}
                    </Button>
                );
            },
        },
        { Header: 'Partner Lot', accessor: 'partnerLotNumber' },
        { Header: 'Item Name', accessor: 'itemName' },
        { Header: 'External Sku', accessor: 'externalSku' },
        { Header: 'Partner Sku', accessor: 'partnerSku' },
        { Header: 'Expiration Date', accessor: 'expirationDate' },
        {
            Header: 'Location',
            accessor: 'locationName',
            Cell: ({ row }) => {
                return (
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => handleLocationLinkClick(row.original.locationName)}
                    >
                        {row.original.locationName}
                    </Button>
                );
            },
        },
        {
            Header: 'Pallets',
            accessor: 'pallets',
            Cell: ({ row }) => {
                const numPallets = row.original.pallets;
                return (
                    <Button
                        disabled={!numPallets}
                        onClick={() => {
                            showPalletModal(row.original);
                        }}
                    >
                        {!numPallets ? <span>0</span> : <strong>{numPallets}</strong>}
                    </Button>
                );
            },
        },
        { Header: 'Units per Case', accessor: 'unitsPerCase' },
        { Header: 'Cases', accessor: 'cases' },
        { Header: 'Loose Units', accessor: 'looseUnits' },
        { Header: 'Total Units', accessor: 'totalUnits' },
        { Header: 'PO', accessor: 'po' },
        { Header: 'Received Date', accessor: 'receivedDate' },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <ActionsCell row={row} setSelectedRow={setSelectedRow} setViewActions={setViewActions} />
            ),
        },
    ];

    const actionDefinitions = [
        {
            text: 'Bulk Adjust Units per Case',
            value: 'bulkAdjustUnitsPerCase',
            componentJSX: function () {
                return (
                    <BulkAdjustUnitsPerCaseAction
                        lotId={selectedRow?.lotId}
                        unitsPerCase={selectedRow?.unitsPerCase}
                        onSuccess={() => {
                            handleSuccess(false);
                        }}
                        onCancel={handleModalClose}
                    />
                );
            },
        },
        {
            text: 'Adjust Cases and Loose Units',
            value: 'adjustCasesAndLooseUnits',
            componentJSX: function () {
                return (
                    <AdjustCasesAndLooseUnitsAction
                        locationId={selectedRow?.locationId}
                        lotId={selectedRow?.lotId}
                        caseNum={selectedRow?.cases}
                        looseUnitNum={selectedRow?.looseUnits}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                        locInvType={selectedRow?.locInvType}
                        palletNum={selectedRow?.pallets}
                        unitsPerCase={selectedRow?.unitsPerCase}
                    />
                );
            },
        },
        {
            text: 'Move Cases and Loose Units',
            value: 'moveCaseAndLooseUnits',
            componentJSX: function () {
                return (
                    <MoveCasesAndLooseUnitsFromKnownOriginAction
                        lotId={selectedRow?.lotId}
                        locationName={selectedRow?.location}
                        locationId={selectedRow?.locationId}
                        locInvType={selectedRow?.locInvType}
                        onSuccess={handleSuccess}
                        numPallets={selectedRow?.pallets}
                        cases={selectedRow?.cases}
                        units={selectedRow?.looseUnits}
                        unitsPerCase={selectedRow?.unitsPerCase}
                    />
                );
            },
        },
        {
            text: 'Print Case Label',
            value: 'printCaseLabel',
            componentJSX: function () {
                return <PrintCaseLabelAction lotId={selectedRow?.lotId} onSuccess={handleSuccess} />;
            },
        },
        {
            text: 'Change Expiration Date',
            value: 'changeExpirationDate',
            componentJSX: function () {
                return (
                    <ChangeExpirationDateAction
                        lotId={selectedRow?.lotId}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                        expLifeDays={selectedRow?.expireLifeDays ?? null}
                    />
                );
            },
        },
        {
            text: 'Update Partner Lot Number',
            value: 'updatePartnerLotNumber',
            componentJSX: function () {
                return (
                    <UpdatePartnerLotAction
                        lotId={selectedRow?.lotId}
                        onSuccess={handleSuccess}
                        partnerLotNumber={selectedRow?.partnerLotNumber}
                    />
                );
            },
        },
    ];

    //API call to GET data
    const getLotDataForSku = async () => {
        if (!loadingLots && isMounted.current) {
            setLoadingLots(true);
            const searchValues = [
                { searchBy: 'externalSku', searchTerm: externalSku },
                { searchBy: 'type', searchTerm: type },
                { searchBy: 'groupByLocation', searchTerm: true },
            ];
            const result = await warehouseInventoryV2Api.getLots(user.getFacilityID(), searchValues);
            if (result.status) {
                setRows(
                    result.data.lots.map((lot) => {
                        return {
                            ...lot,
                            looseUnits: lot.numLooseUnits,
                            cases: lot.numCases,
                            pallets: lot.numPallets,
                            po: lot.purchaseOrderId,
                            active: lot.totalUnits > 0,
                            location: lot.locationName,
                            locInvType: lot.inventoryType,
                        };
                    }),
                );
            } else {
                setNotification({ text: result.msg, level: 'error' });
            }
            setLoadingLots(false);
        }
    };

    const handleLocationLinkClick = (locationName) => {
        onLinkClicked({ tabIndex: 1, searchBy: 'locationName', searchTerm: locationName });
    };

    const handleLotLinkClick = (lotId) => {
        onLinkClicked({ tabIndex: 0, searchBy: 'lotId', searchTerm: lotId });
    };

    const handleModalClose = () => {
        setViewActions(false);
        setSelectedRow({});
    };

    const handleViewPalletModalClose = () => {
        setSelectedLotId(null);
        setSelectedLocationId(null);
        setSelectedLocationName('');
    };

    const handleSuccess = (closeModal = true) => {
        onSuccess();
        if (closeModal) {
            handleModalClose();
        }
        getLotDataForSku();
    };

    return (
        <>
            <Notifications options={notification} />
            <div className="expanded-table-div">
                {loadingLots && <CircularProgress color="inherit" size={48} />}
                {!loadingLots && (
                    <DataTable
                        columns={columns}
                        data={rows}
                        editable={false}
                        tableContainerMaxHeight={600}
                        initialSortBy={[
                            {
                                id: 'expirationDate',
                                desc: false,
                            },
                        ]}
                    />
                )}
                <ActionModal
                    title={`Actions for Lot ${selectedRow.lotId}`}
                    open={viewActions}
                    row={selectedRow}
                    actions={actions}
                    closeModal={handleModalClose}
                />
                <ViewPalletsModal
                    open={selectedLotId > 0 && selectedLocationId > 0}
                    closeModal={handleViewPalletModalClose}
                    lotId={selectedLotId}
                    locationId={selectedLocationId}
                    locationName={selectedLocationName}
                    setNotification={setNotification}
                    onMovePalletSuccess={handleMovePalletSuccess}
                />
            </div>
        </>
    );
};

export { SkuTabExpansion };
